import React, { lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import StatusDoneIcon from '@assets/buttons-icons/status-done.svg?react';
import StatusPendingIcon from '@assets/buttons-icons/status-pending.svg?react';
import GoogleCalendarIcon from '@assets/third-party-logos/google/google-calendar.svg?react';
import MicrosoftCalendarIcon from '@assets/third-party-logos/microsoft/microsoft-calendar.svg?react';
import successAnimationData from '@lib/core/src/assets/lottie/check-success.json?url';
import ToastVariant from '@lib/core/src/enums/ToastVariant';
import palette from '@lib/core/src/theme/palette';
import { Player } from '@lottiefiles/react-lottie-player';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { RiThumbDownLine } from '@remixicon/react';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

import { EVENTS } from '@/analytics/consts';
import { useAnalytics } from '@/analytics/useAnalytics';
import {
  NylasGrantDtoProvider,
  SourceType,
  TaskDto,
  TaskDtoInvalidityReason,
  TaskDtoStatus,
  TaskDtoValidity
} from '@/api/generated';
import { toMeeting } from '@/enums/RoutesMap';
import useCalendar from '@/hooks/useCalendar';
import useMeetings from '@/hooks/useMeetings';
import useModal from '@/hooks/useModal/useModal';
import useTasks from '@/hooks/useTasks';

import TaskDismissNotification from './TaskDismissNotification';
import {
  ActionButton,
  Container,
  Content,
  DismissButton,
  SourceTooltip,
  TaskActionDisbaledTitle,
  TaskActions,
  TaskActionTitle,
  TaskTitle
} from './TaskRowStyles';
import TaskSourceTooltip from './TaskSourceTooltip';
import { TaskTitleEditNotification } from './TaskTitleEditNotification';

const LazyAddToAgendaModal = lazy(
  () => import('@/components/modals/AddToAgendaModal/AddToAgendaModal')
);
const LazyDelegateModal = lazy(() => import('@/components/modals/DelegateModal/DelegateModal'));
const ICON_SIZE = '16px';

const TaskRow = ({
  task,
  provider,
  refetchTasks
}: {
  task: TaskDto;
  provider: NylasGrantDtoProvider | undefined;
  refetchTasks: () => void;
}) => {
  const { captureEvent } = useAnalytics();
  const [hovered, setHovered] = useState(false);
  const [title, setTitle] = useState(task.title);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const titleRef = useRef<HTMLInputElement | null>(null);
  const isDelegated = task?.status === TaskDtoStatus.delegated;
  const { showModal } = useModal();
  const navigate = useNavigate();

  const { meeting } = useMeetings(undefined, undefined, task?.source?.sourceId);

  const { patchTask } = useTasks();
  const { event, eventLoading } = useCalendar(undefined, undefined, meeting?.calendarEventId);

  const onStatusClicked = useCallback(() => {
    if (task?.id && !isAnimating) {
      const newStatus =
        task.status === TaskDtoStatus.done ? TaskDtoStatus.pending : TaskDtoStatus.done;
      if (newStatus === TaskDtoStatus.done) {
        captureEvent(EVENTS.TASK_MARKED_DONE, {
          taskId: task.id,
          taskTitle: task.title,
          taskSourceId: task.source?.sourceId
        });
        setIsAnimating(true);
      }

      patchTask(task.id, { status: newStatus })
        .catch(error => {
          enqueueSnackbar('Failed to update task status', { variant: 'error' });
          if (newStatus === TaskDtoStatus.done) {
            setIsAnimating(false);
          }
        })
        .finally(() => setTimeout(() => refetchTasks(), 500));
    }
  }, [task, patchTask, isAnimating, refetchTasks]);

  const onAddToAgenda = useCallback(() => {
    const modal = showModal(LazyAddToAgendaModal, {
      onClose: () => {
        modal.hide();
      },
      content: task.title,
      type: 'Task',
      entityId: task.id
    });
  }, [showModal, task]);

  const onDelegate = useCallback(() => {
    const modal = showModal(LazyDelegateModal, {
      onClose: () => {
        modal.hide();
      },
      task: task
    });
  }, [showModal, task]);

  const onDismiss = () => {
    if (!task.id) return;
    captureEvent(EVENTS.TASK_DISMISSED, {
      taskId: task.id,
      taskTitle: task.title,
      taskSourceId: task.source?.sourceId
    });
    patchTask(task.id, {
      validity: TaskDtoValidity.invalid,
      invalidityReason: TaskDtoInvalidityReason.notRelevant
    }).then(() => refetchTasks());

    enqueueSnackbar({
      variant: 'custom',
      autoHideDuration: 6000,
      anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      customProps: {
        sx: {
          padding: '12px 12px 0px 12px',
          '& .MuiAlert-message': { padding: '0px' },
          borderRadius: '16px',
          alignItems: 'start',
          backgroundColor: palette.black
        },
        caption: (
          <TaskDismissNotification task={task} onClose={() => closeSnackbar()} duration={6000} />
        ),
        variant: ToastVariant.INFO
      }
    });
  };

  const updateTitle = () => {
    if (!task?.id || task?.title === title) return;
    patchTask(task.id, { title: title })
      .then(() => {
        refetchTasks();
        enqueueSnackbar({
          variant: 'custom',
          autoHideDuration: 6000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
          customProps: {
            sx: {
              '& .MuiAlert-message': { padding: '0px', flex: 1 },
              borderRadius: '48px',
              backgroundColor: palette.black
            },
            caption: <TaskTitleEditNotification onClose={() => closeSnackbar()} />,
            variant: ToastVariant.INFO
          }
        });
      })
      .catch(error => {
        enqueueSnackbar('Failed to update task title', { variant: 'error' });
        // Optionally revert title state
        // setTitle(task.title);
      });
  };

  const onTitleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
    }
  };

  useEffect(() => {
    const checkOverflow = () => {
      if (titleRef.current) {
        setIsTruncated(titleRef.current.scrollWidth > titleRef.current.clientWidth);
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [title]);

  return (
    <Container onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <Content>
        <Stack width="100%" direction="row" justifyContent="space-between" alignItems="center">
          <Stack width="100%" direction="row" alignItems="center" gap="8px">
            {/* Container for Icon and Lottie */}
            <Box
              sx={{
                position: 'relative',
                width: ICON_SIZE,
                height: ICON_SIZE,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {isAnimating && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 2
                  }}
                >
                  <Player
                    autoplay
                    loop={false}
                    speed={1.5}
                    src={successAnimationData}
                    style={{ width: 20, height: 20 }}
                    onEvent={event => {
                      if (event === 'complete') {
                        setIsAnimating(false);
                      }
                    }}
                  />
                </Box>
              )}
              {!isAnimating && (
                <IconButton
                  onClick={onStatusClicked}
                  sx={{
                    padding: 0,
                    width: ICON_SIZE,
                    height: ICON_SIZE,
                    zIndex: 1,
                    ':hover': { svg: { path: { fill: palette.title.grey } } }
                  }}
                >
                  {task.status === TaskDtoStatus.done ? (
                    <StatusDoneIcon width={ICON_SIZE} height={ICON_SIZE} />
                  ) : (
                    <StatusPendingIcon width={ICON_SIZE} height={ICON_SIZE} />
                  )}
                </IconButton>
              )}
            </Box>

            <Tooltip title={isTruncated ? title : ''} arrow placement="top">
              <TaskTitle
                inputRef={titleRef}
                value={title}
                onChange={event => {
                  setTitle(event.currentTarget.value);
                }}
                onKeyDown={onTitleKeyDown}
                onBlur={updateTitle}
                sx={{
                  textDecoration: task.status === TaskDtoStatus.done ? 'line-through' : 'none',
                  color: task.status === TaskDtoStatus.done ? palette.grey[500] : 'inherit'
                }}
              />
            </Tooltip>
          </Stack>
        </Stack>

        {hovered && (
          <Stack direction="row" width="100%" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" gap="12px">
              <Stack>
                {task.source?.type === SourceType.meeting && (
                  <SourceTooltip
                    title={
                      event ? (
                        <TaskSourceTooltip
                          onClick={() => meeting?.id && navigate(toMeeting(meeting?.id))}
                          loading={eventLoading}
                          title={`${event.title} (${event.when?.startTime ? new Date(event.when.startTime * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''})`}
                          description={task.source.context}
                        />
                      ) : (
                        ''
                      )
                    }
                    arrow
                  >
                    <Stack>
                      {provider === NylasGrantDtoProvider.google ? (
                        <GoogleCalendarIcon />
                      ) : (
                        <MicrosoftCalendarIcon />
                      )}
                    </Stack>
                  </SourceTooltip>
                )}

                {!task.source?.type && <div style={{ minWidth: 16 }}></div>}
              </Stack>

              <TaskActions>
                {isDelegated ? (
                  <TaskActionDisbaledTitle>Delegated</TaskActionDisbaledTitle>
                ) : (
                  task?.source && (
                    <ActionButton
                      onClick={onDelegate}
                      disabled={task.status === TaskDtoStatus.done}
                    >
                      <TaskActionTitle>Delegate</TaskActionTitle>
                    </ActionButton>
                  )
                )}
                <ActionButton onClick={onAddToAgenda} disabled={task.status === TaskDtoStatus.done}>
                  <TaskActionTitle>Add to agenda</TaskActionTitle>
                </ActionButton>
              </TaskActions>
            </Stack>

            <DismissButton onClick={onDismiss}>
              <RiThumbDownLine size={16} color={palette.grey[400]} />
            </DismissButton>
          </Stack>
        )}
      </Content>
    </Container>
  );
};

export default TaskRow;
